import React from "react";
import { useRouter } from "next/router";
import styled, { createGlobalStyle } from "styled-components";
import { ALink, FlexContainer, FlexDiv } from "@/components/Atoms/Box";
import { Typography } from "@/foundation/Typography/V1";
import theme from "@/foundation/Theme";
import { space } from "styled-system";
import useDisableIntercom from "@/hooks/useDisableIntercom";

export const MedicalPageContent = (props) => {
  const { showHeader = false } = props || {};

  const location = useRouter();
  const isFromAppSource = location?.query;
  const showTopSpace = isFromAppSource?.source !== "app";

  return (
    <Container flexDirection={"column"}>
      {showHeader && (
        <Typography
          fontFamily={"var(--font-500)"}
          color={theme.colors.gray_100}
          variant="heading1"
        >
          Medical Waiver &amp; Disclaimers
        </Typography>
      )}
      <Wrapper flexDirection={"column"} showHeightGap={showTopSpace}>
        <Typography
          variant="paragraph2"
          fontFamily={"var(--font-400)"}
          color={theme.colors.gray_100}
        >
          Please read the below medical waiver and disclaimers carefully before
          using the Platform or accessing any material, information or Platform
          Services through the Platform.
        </Typography>
        <Heading
          variant="paragraph2"
          fontFamily={"var(--font-400)"}
          color={theme.colors.gray_100}
        >
          Our Products &amp; Services
        </Heading>
        <Typography
          variant="paragraph2"
          fontFamily={"var(--font-400)"}
          color={theme.colors.gray_100}
        >
          Please note that the headings herein are included for convenience and
          identification only, and are not intended to describe, interpret,
          define or limit the scope, extend or intend of these Terms.
        </Typography>
        <Typography
          variant="paragraph2"
          fontFamily={"var(--font-400)"}
          color={theme.colors.gray_100}
        >
          You acknowledge that the Platform allows you to avail services / buy
          products directly from the Company as well as from various trainers
          engaged by the Company, through online and offline mode. We offer
          services through (i) offline mode in identified studios and fitness
          centres (operated by the Company) and at User's residence or office
          premises (<Strong>“Fitness Classes”</Strong>) as well as through (ii)
          online mode through our website www.betheshyft.com and the mobile
          application developed and operated by the us (
          <Strong>“App-based Products and Services”</Strong>) (collectively,{" "}
          <Strong>“Fitness Services”</Strong>). The products and services
          available on the Platform are listed below:
        </Typography>
        <UL>
          <BulletList>
            <Typography
              variant="paragraph2"
              fontFamily={"var(--font-400)"}
              color={theme.colors.gray_100}
            >
              Purchase and avail physical and mental fitness centre
              memberships/classes and allied services, including physical
              fitness, mindful yoga and meditation related training activities,
              under the brand name 'Betheshyft', for both Fitness Classes and
              App-based Products and Services;
            </Typography>
          </BulletList>
          <BulletList>
            <Typography
              variant="paragraph2"
              fontFamily={"var(--font-400)"}
              color={theme.colors.gray_100}
            >
              Booking appointments / sessions, for both Fitness Classes and
              App-based Products and Services (
              <Strong>“Booking Services”</Strong>);
            </Typography>
          </BulletList>
          <BulletList>
            <Typography
              variant="paragraph2"
              fontFamily={"var(--font-400)"}
              color={theme.colors.gray_100}
            >
              Procurement and / or generation, reproduction on the Platform,
              sharing with relevant Vendors (
              <ItalicText>defined below</ItalicText>) for you, if applicable,
              and communication to you of (i) your indicative usage and / or
              consumption data of Fitness Services and Booking Services; and
              (ii) any documents or data generated from your purchase and / or
              usage and / or consumption of aforementioned products and / or
              services, separately as well as jointly with other products and /
              or services listed on the Platform, including transaction
              summaries, invoices, reports, scores, achievements, etc. (
              <Strong>“Diverse Services”</Strong>);
            </Typography>
          </BulletList>
        </UL>
        <Typography
          variant="paragraph2"
          fontFamily={"var(--font-400)"}
          color={theme.colors.gray_100}
        >
          Fitness Services, Booking Services and Diverse Services shall be
          collectively referred to as “Platform Services” and shall be provided
          by the Company and third parties with whom the Company has contractual
          arrangements (<Strong>“Vendors”</Strong>);
        </Typography>
        <OL pl={"0px"}>
          <LI>
            <Typography
              fontFamily={"var(--font-400)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              In the interest of your physical and mental wellbeing, you
              understand and acknowledge that
            </Typography>
            <OL>
              <LI>
                <Typography
                  fontFamily={"var(--font-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Inherent risks:</Strong> The Fitness Services offered
                  herein, by their very nature, include certain inherent risks
                  that cannot be eliminated regardless of the care taken to
                  avoid injuries and/or any kind of losses. You hereby assert
                  that your participation is voluntary and that you knowingly
                  assume all such risks and hence hereby release the Released
                  Party (as defined below) of all liability arising out of such
                  aforementioned risks;
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--font-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Illness/Injury:</Strong> The Fitness Services offered
                  herein, by their very nature, include certain inherent risks
                  that cannot be eliminated regardless of the care taken to
                  avoid injuries and/or any kind of losses. You hereby assert
                  that your participation is voluntary and that you knowingly
                  assume all such risks and hence hereby release the Released
                  Party (<ItalicText>as defined below</ItalicText>) of all
                  liability arising out of such aforementioned risks;
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--font-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  You will not participate in the Platform Services under the
                  influence of heavy medication, drugs, alcohol or other
                  mood-altering substances, or undertake the exercises/training
                  offered through our App-based Products and Services while
                  driving or operating any machinery.
                </Typography>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--font-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>
                    Consultation by physician/healthcare provider:
                  </Strong>
                </Typography>
                <OL>
                  <LI>
                    <Typography
                      fontFamily={"var(--font-400)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      You would consult your physician or health care provider
                      before enrolling for the Fitness Services offered by us/
                      booking our classes, in the event you are or
                      expect/suspect to (a) be pregnant, or (b) wear a
                      pacemaker, or (c) suffer from epilepsy or seizures, or (d)
                      have a history of mental illness, or (e) have eating
                      disorder, or (f) have diabetes, or (g) have any other
                      physical/ psychological/ emotional/ medical condition, or
                      (h) are taking any strong medication.
                    </Typography>
                  </LI>
                  <LI>
                    <Typography
                      fontFamily={"var(--font-400)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      You will obtain the approval of your health care
                      provider/doctor before participating in the Platform
                      Services, in the event you are over 65 years of age.
                    </Typography>
                  </LI>
                </OL>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--font-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Medical Aid:</Strong> There is no obligation for any
                  person to provide you with medical care while performing the
                  activities offered under the Platform Services. You further
                  understand and acknowledge that:
                </Typography>
                <OL>
                  <LI>
                    <Typography
                      fontFamily={"var(--font-400)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      there may be no aid stations available for the said
                      activity; and
                    </Typography>
                  </LI>
                  <LI>
                    <Typography
                      fontFamily={"var(--font-400)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      if medical care is rendered to you, you consent to that
                      care if you are unable to give your consent for any reason
                      at the time the care is rendered.
                    </Typography>
                  </LI>
                </OL>
              </LI>
              <LI>
                <Typography
                  fontFamily={"var(--font-400)"}
                  variant="paragraph2"
                  color={theme.colors.gray_100}
                >
                  <Strong>Disclaimer by Company: </Strong>
                </Typography>
                <OL>
                  <LI>
                    <Typography
                      fontFamily={"var(--font-400)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      The Company makes no claims, representations or guarantees
                      that the Platform Services offered would provide a
                      therapeutic benefit to all its Users. We hereby disclaim
                      any guarantees of exactness as to the duration, type,
                      satisfaction from the Fitness Services provided by us.
                    </Typography>
                  </LI>
                  <LI>
                    <Typography
                      fontFamily={"var(--font-400)"}
                      variant="paragraph2"
                      color={theme.colors.gray_100}
                    >
                      The Company is not a health care provider or a medical
                      device provider and that our Fitness Services are neither
                      medical/health care advice, nor a replacement for, or are
                      a form of, therapy, and that the Fitness Services are not
                      intended to cure, treat, or diagnose any medical
                      conditions which a User may have.
                    </Typography>
                  </LI>
                </OL>
              </LI>
            </OL>
          </LI>
          <LI>
            <Typography
              fontFamily={"var(--font-400)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              You, for yourself and for your heirs, next of kin, executors,
              administrators and assigns, fully release, waive and forever
              discharge any and all rights or claims of any nature whatsoever,
              including but not limited to any claims pertaining to a physical
              or mental injury or illness, or claims arising out of negligence
              of any Released Party (as defined below) or anyone else, that you
              may have now or in the future, against the Company, any of its
              affiliates, franchisees and their respective representatives,
              directors, officers, independent contractors, consultants, agents,
              employees or volunteer staff (<Strong>“Released Party”</Strong>),
              in relation to or in connection with or arising out of the
              availing of Platform Services by you. Without limiting the
              foregoing, you further release any recourses which you may, now or
              hereafter, have resulting from any decision of any Released Party.
            </Typography>
          </LI>
          <LI>
            <Typography
              fontFamily={"var(--font-400)"}
              variant="paragraph2"
              color={theme.colors.gray_100}
            >
              You agree to indemnify (reimburse for any loss) and hold harmless
              each Released Party from any loss or liability (including any
              reasonable legal fees they may incur) defending any claim made by
              you or anyone making a claim on your behalf, even if the claim is
              alleged to or did result from the carelessness or negligence of
              any Released Party or anyone else.
            </Typography>
          </LI>
        </OL>
      </Wrapper>
      <GlobalCss />
    </Container>
  );
};
function MedicalPage() {
  useDisableIntercom({});
  return <MedicalPageContent />;
}

export default MedicalPage;
const Heading = styled(Typography)`
  text-decoration: underline;
  font-family: var(--font-400);
`;
const Container = styled(FlexContainer)`
  margin: 40px auto auto;
`;
const AlphaList = styled.li`
  list-style-type: ${({ capital }) =>
    capital ? "upper-alpha" : "lower-alpha"};
  margin-bottom: 12px;
  &:last-child {
    margin-bottom: 0px;
  }
`;
const RomanList = styled.li`
  list-style-type: lower-roman;
  margin-bottom: 12px;
  &:last-child {
    margin-bottom: 0px;
  }
`;
const BulletList = styled.li`
  list-style-type: disc;
  margin-bottom: 12px;
  &:last-child {
    margin-bottom: 0px;
  }
`;
const Wrapper = styled(FlexDiv)`
  padding: ${(props) => {
    return props.showHeightGap ? "110px 0px" : "10px 0px";
  }};
  p {
    margin-bottom: 11px;
    &:last-child {
      margin-bottom: 0px;
    }
  }
`;
const OL = styled.ol`
  ${space}
  ol {
    padding-inline-start: 40px !important;
  }
  counter-reset: item;
  > li {
    padding-left: 0px;
    li {
      padding-left: 0px;
    }
    ${AlphaList} {
      padding-left: 0;
    }
    ${Heading} {
      margin-bottom: 25px;
    }
  }
`;
const UL = styled.ul`
  ${space}
  ol {
    padding-inline-start: 40px !important;
  }
  counter-reset: item;
  > li {
    padding-left: 0px;
    li {
      padding-left: 0px;
    }
    ${AlphaList} {
      padding-left: 0;
    }
    ${Heading} {
      margin-bottom: 25px;
    }
  }
`;
const LI = styled.li`
  ${space}
  display: block;
  margin-bottom: 12px;
  &:last-child {
    margin-bottom: 0px;
  }
  position: relative;
  &:before {
    display: inline-block;
    float: left;
    margin-right: 5px;
    content: counters(item, ".") ".";
    counter-increment: item;
    font-size: 12px;
    font-family: var(--font-400);
    color: ${theme.colors.gray_100};
    @media only screen and (min-width: 640px) {
      font-size: 14px;
    }
    @media only screen and (min-width: 960px) {
      font-size: 16px;
    }
  }
`;
const Strong = styled.strong`
  font-size: inherit;
  font-family: var(--font-700);
  color: inherit;
`;
const SUBOL = styled(OL)`
  counter-reset: item;
`;
const Link = styled(ALink)`
  font-size: inherit;
  font-family: inherit;
  color: ${theme.colors.dotsColor};
  ${({ isNormal }) =>
    !isNormal &&
    `
    font-style: italic;
  `};
  &:hover {
    color: ${theme.colors.dotsColor};
  }
`;
const ItalicText = styled.i`
  font-size: inherit;
  font-family: inherit;
  color: inherit;
  font-style: italic;
`;
const GlobalCss = createGlobalStyle`
::marker {
    font-size: 12px;
    color: ${theme.colors.gray_100};
    font-family: 'Raleway',sans-serif;
    @media only screen and (min-width: 640px) {
    font-size: 14px;
    }
    @media only screen and (min-width: 960px) {
    font-size: 16px;
    }
}
  `;
